import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Send, Edit2, Check, X, Mail, Truck, Search, RefreshCw, CheckCircle, AlertTriangle, FlaskConical, Building2, Calendar, Settings, Plus, Trash2, Save,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function APEmailPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [couriers, setCouriers] = useState([]);
  const [defaultCourier, setDefaultCourier] = useState("");
  const [hoEmail, setHoEmail] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [selected, setSelected] = useState([]);
  const [editingAwb, setEditingAwb] = useState(null);
  const [awbInput, setAwbInput] = useState("");
  const [courierInput, setCourierInput] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingOne, setSendingOne] = useState(null);
  const [sendingHq, setSendingHq] = useState(false);
  const [sendingTestHq, setSendingTestHq] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [showSettings, setShowSettings] = useState(false);

  const invoiceDates = useMemo(() => {
    return [...new Set(invoices.map(inv => {
      const d = new Date(inv.created_at || inv.invoice_date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }))].sort((a, b) => new Date(b) - new Date(a));
  }, [invoices]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/ap/invoice`); setInvoices(res.data || []); } catch {}
    setLoading(false);
  }, []);

  const loadCouriers = async () => {
    try {
      const res = await axios.get(`${API}/ap/courier-settings`);
      setCouriers(res.data.couriers || []);
      setDefaultCourier(res.data.default_courier || "");
      setHoEmail(res.data.ho_email || "");
      setTestEmail(res.data.test_email || "");
    } catch {}
  };

  useEffect(() => { loadInvoices(); loadCouriers(); }, [loadInvoices]);

  const filtered = invoices.filter(inv => {
    if (dateFilter !== "all") {
      const d = new Date(inv.created_at || inv.invoice_date);
      const invDate = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
      if (invDate !== dateFilter) return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (inv.invoice_number || "").toLowerCase().includes(q) || (inv.po_number || "").toLowerCase().includes(q) ||
           (inv.buyer_store || "").toLowerCase().includes(q) || (inv.awb_number || "").toLowerCase().includes(q);
  });

  const selectableInFiltered = filtered.filter(i => !i.email_sent);
  const allFilteredSelected = selectableInFiltered.length > 0 && selectableInFiltered.every(i => selected.includes(i.id));
  const someFilteredSelected = selectableInFiltered.some(i => selected.includes(i.id)) && !allFilteredSelected;

  const toggleAll = () => {
    if (selectableInFiltered.length === 0) return;
    if (allFilteredSelected) {
      setSelected(prev => prev.filter(id => !selectableInFiltered.map(i => i.id).includes(id)));
    } else {
      const newIds = selectableInFiltered.map(i => i.id).filter(id => !selected.includes(id));
      setSelected(prev => [...prev, ...newIds]);
    }
  };
  const toggleOne = (id) => {
    const inv = invoices.find(i => i.id === id);
    if (inv?.email_sent) return;
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const startEdit = (inv) => { setEditingAwb(inv.id); setAwbInput(inv.awb_number || ""); setCourierInput(inv.courier || defaultCourier); };

  const saveAwb = async (invoiceId) => {
    try { await axios.put(`${API}/ap/invoice/${invoiceId}/awb`, { awb_number: awbInput, courier: courierInput }); toast.success("AWB saved"); setEditingAwb(null); loadInvoices(); }
    catch { toast.error("Failed to save AWB"); }
  };

  const sendOne = async (invId) => {
    const inv = invoices.find(i => i.id === invId);
    if (!inv?.awb_number || !inv?.courier) { toast.error("Set AWB and courier first"); return; }
    setSendingOne(invId);
    try {
      const res = await axios.post(`${API}/ap/dispatch/send-emails`, { invoice_ids: [invId], head_office_email: "" });
      if (res.data.total_sent > 0) toast.success(`Email sent to ${res.data.store_emails[0]?.to || "store"}`);
      else toast.error(res.data.store_emails[0]?.error || "Failed to send");
      loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    setSendingOne(null);
  };

  const sendBulk = async () => {
    if (!selected.length) { toast.error("Select invoices first"); return; }
    const missing = invoices.filter(i => selected.includes(i.id) && (!i.awb_number || !i.courier));
    if (missing.length > 0) { toast.error(`${missing.length} selected invoice(s) missing AWB or courier`); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/ap/dispatch/send-emails`, { invoice_ids: selected, head_office_email: hoEmail });
      toast.success(`Sent ${res.data.total_sent} email(s)${res.data.total_failed > 0 ? `, ${res.data.total_failed} failed` : ""}`);
      if (res.data.head_office?.success) toast.success("Head office summary sent");
      setSelected([]); loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("No test email configured"); return; }
    const withAwb = invoices.find(i => i.awb_number && i.courier);
    if (!withAwb) { toast.error("No invoice with AWB found"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/ap/dispatch/send-emails`, { invoice_ids: [withAwb.id], head_office_email: "", override_recipient: testEmail });
      if (res.data.total_sent > 0) toast.success(`Test email sent to ${testEmail}`);
      else toast.error(res.data.store_emails[0]?.error || "Test failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Test failed"); }
    setSending(false);
  };

  const sendHqSummary = async () => {
    if (!hoEmail) { toast.error("No HQ email configured"); return; }
    const pool = selected.length > 0 ? invoices.filter(i => selected.includes(i.id) && i.awb_number && i.courier) : invoices.filter(i => i.awb_number && i.courier);
    if (!pool.length) { toast.error("No invoices with AWB ready"); return; }
    setSendingHq(true);
    try {
      const res = await axios.post(`${API}/ap/dispatch/send-emails`, { invoice_ids: pool.map(i => i.id), head_office_email: hoEmail, skip_store_emails: true });
      if (res.data.head_office?.success) toast.success(`HQ summary sent to ${hoEmail} (${pool.length} invoices)`);
      else toast.error(res.data.head_office?.error || "HQ email failed");
      loadInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || "HQ send failed"); }
    setSendingHq(false);
  };

  const sendTestHqSummary = async () => {
    if (!testEmail) { toast.error("No test email configured"); return; }
    const pool = selected.length > 0 ? invoices.filter(i => selected.includes(i.id) && i.awb_number && i.courier) : invoices.filter(i => i.awb_number && i.courier);
    if (!pool.length) { toast.error("No invoices with AWB ready"); return; }
    setSendingTestHq(true);
    try {
      const res = await axios.post(`${API}/ap/dispatch/send-emails`, { invoice_ids: pool.map(i => i.id), head_office_email: testEmail, skip_store_emails: true, override_recipient: testEmail });
      if (res.data.head_office?.success) toast.success(`Test HQ summary sent to ${testEmail}`);
      else toast.error(res.data.head_office?.error || "Test HQ failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Test HQ failed"); }
    setSendingTestHq(false);
  };

  const readyCount = filtered.filter(i => i.awb_number && i.courier).length;
  const sentCount = filtered.filter(i => i.email_sent).length;
  const hqSentCount = filtered.filter(i => i.hq_email_sent).length;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="ap-email-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Mail className="w-5 h-5" /> AP Email Dispatch</h1>
          <p className="text-sm text-slate-500 mt-0.5">Send PO, Invoice and tracking details to stores</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" size="sm" onClick={sendTest} disabled={sending} title="Sends a sample email to the test address only"><FlaskConical className="w-3.5 h-3.5 mr-1.5" /> Send Test Email</Button>
          <Button variant="outline" size="sm" onClick={sendTestHqSummary} disabled={sendingTestHq} title={selected.length > 0 ? `Test HQ summary for ${selected.length} selected` : "Test HQ summary"}>
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" /> {sendingTestHq ? "Sending..." : selected.length > 0 ? `Test HQ (${selected.length})` : "Test HQ Email"}
          </Button>
          <Button size="sm" onClick={sendHqSummary} disabled={sendingHq} className="bg-violet-600 hover:bg-violet-700 text-white" title={selected.length > 0 ? `HQ summary for ${selected.length} selected` : "Full dispatch summary to HQ"}>
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> {sendingHq ? "Sending..." : selected.length > 0 ? `Send HQ (${selected.length})` : "Send HQ Email"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { loadInvoices(); loadCouriers(); }}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)} data-testid="ap-email-settings-btn"><Settings className="w-3.5 h-3.5 mr-1.5" /> Settings</Button>
        </div>
      </div>

      {/* Courier & Email Settings */}
      {showSettings && <APEmailSettings couriers={couriers} defaultCourier={defaultCourier} hoEmail={hoEmail} testEmail={testEmail} onSaved={() => { loadCouriers(); setShowSettings(false); }} />}

      {/* Status Bar */}
      <div className="flex gap-3 text-xs flex-wrap">
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-lg"><span className="text-slate-500">Total:</span><span className="font-bold text-slate-800">{invoices.length}</span></div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg"><CheckCircle className="w-3 h-3 text-emerald-500" /><span className="text-slate-500">AWB ready:</span><span className="font-bold text-emerald-700">{readyCount}</span></div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg"><Mail className="w-3 h-3 text-blue-500" /><span className="text-slate-500">Store emailed:</span><span className="font-bold text-blue-700">{sentCount}</span></div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 rounded-lg"><Building2 className="w-3 h-3 text-violet-500" /><span className="text-slate-500">HQ emailed:</span><span className="font-bold text-violet-700">{hqSentCount}</span></div>
        {hoEmail && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 rounded-lg"><Building2 className="w-3 h-3 text-violet-500" /><span className="text-slate-500">HQ:</span><span className="font-medium text-violet-700">{hoEmail}</span></div>}
        {testEmail && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-lg"><FlaskConical className="w-3 h-3 text-amber-500" /><span className="text-slate-500">Test:</span><span className="font-medium text-amber-700">{testEmail}</span></div>}
      </div>

      {/* Bulk Action Bar */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 bg-slate-800 text-white rounded-lg px-4 py-2.5">
          <span className="text-sm font-medium">{selected.length} selected</span>
          <div className="h-4 w-px bg-white/20" />
          <Button size="sm" onClick={sendBulk} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
            {sending ? "Sending..." : <><Send className="w-3 h-3 mr-1" /> Email Selected ({selected.length})</>}
          </Button>
          {hoEmail && <span className="text-xs text-slate-400">+ summary to {hoEmail}</span>}
          <button onClick={() => setSelected([])} className="ml-auto text-xs text-slate-400 hover:text-white">Clear</button>
        </div>
      )}

      {/* Date Tabs */}
      {invoiceDates.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <Tabs value={dateFilter} onValueChange={setDateFilter}>
            <TabsList className="h-8">
              <TabsTrigger value="all" className="text-xs px-3">All ({invoices.length})</TabsTrigger>
              {invoiceDates.slice(0, 10).map(date => (
                <TabsTrigger key={date} value={date} className="text-xs px-3">
                  {date} ({invoices.filter(inv => { const d = new Date(inv.created_at || inv.invoice_date); return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) === date; }).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Search */}
      <div className="relative w-72">
        <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices, stores, AWB..." className="pl-8 h-8 text-sm" />
      </div>

      {/* Invoice Table */}
      <div className="border rounded-xl overflow-hidden bg-white">
        <div className="max-h-[550px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="w-10"><Checkbox checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false} onCheckedChange={toggleAll} disabled={selectableInFiltered.length === 0} /></TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoice #</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">PO #</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">Store</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">Amount</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">Courier</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">AWB Number</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Email</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-24">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-sm text-slate-400">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-12 text-sm text-slate-400">No invoices found</TableCell></TableRow>
              ) : filtered.map(inv => {
                const storeSent = !!inv.email_sent;
                const hqSent = !!inv.hq_email_sent;
                const fullyDone = storeSent && hqSent;
                return (
                  <TableRow key={inv.id} className={`${fullyDone ? "opacity-50" : ""} hover:bg-slate-50`}>
                    <TableCell><Checkbox checked={selected.includes(inv.id)} onCheckedChange={() => toggleOne(inv.id)} disabled={storeSent} /></TableCell>
                    <TableCell className="font-mono text-xs font-medium text-slate-800">{inv.invoice_number}</TableCell>
                    <TableCell className="text-xs text-slate-500">{inv.po_number || "-"}</TableCell>
                    <TableCell className="text-xs font-medium text-slate-700 max-w-[150px] truncate">{inv.buyer_store || "-"}</TableCell>
                    <TableCell className="text-xs font-mono">Rs. {inv.grand_total?.toLocaleString()}</TableCell>

                    {editingAwb === inv.id ? (<>
                      <TableCell>
                        <select value={courierInput} onChange={e => setCourierInput(e.target.value)} className="h-7 text-xs border rounded px-1 w-full">
                          <option value="">Select courier</option>
                          {couriers.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                        </select>
                      </TableCell>
                      <TableCell>
                        <input value={awbInput} onChange={e => setAwbInput(e.target.value)} placeholder="AWB number" className="h-7 text-xs border rounded px-2 w-full font-mono" onKeyDown={e => e.key === "Enter" && saveAwb(inv.id)} autoFocus />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => saveAwb(inv.id)} className="text-emerald-600 hover:text-emerald-800"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingAwb(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                        </div>
                      </TableCell>
                    </>) : (<>
                      <TableCell className="text-xs">
                        {inv.courier ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-slate-700"><Truck className="w-3 h-3" /> {inv.courier}</span> : <span className="text-slate-300">-</span>}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{inv.awb_number || <span className="text-slate-300">-</span>}</TableCell>
                      <TableCell className="text-center">
                        {fullyDone ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle className="w-3 h-3" /> Complete</span> :
                         storeSent ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle className="w-3 h-3" /> Store sent</span> :
                         hqSent ? <span className="inline-flex items-center gap-1 text-[11px] text-blue-600"><CheckCircle className="w-3 h-3" /> HQ sent</span> :
                         inv.awb_number ? <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><AlertTriangle className="w-3 h-3" /> Pending</span> :
                         <span className="text-[11px] text-slate-300">No AWB</span>}
                      </TableCell>
                    </>)}

                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {editingAwb !== inv.id && !fullyDone && (
                          <button onClick={() => startEdit(inv)} className="text-xs text-blue-500 hover:text-blue-700 font-medium px-1.5 py-0.5 hover:bg-blue-50 rounded"><Edit2 className="w-3 h-3" /></button>
                        )}
                        {inv.awb_number && inv.courier && editingAwb !== inv.id && !storeSent && (
                          <button onClick={() => sendOne(inv.id)} disabled={sendingOne === inv.id} className="text-xs text-emerald-600 hover:text-emerald-800 font-medium px-1.5 py-0.5 hover:bg-emerald-50 rounded">
                            {sendingOne === inv.id ? "..." : <Send className="w-3 h-3" />}
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="text-xs text-slate-400 space-y-0.5">
        <p>Store emails are sent to addresses from the AP Store Contacts. Stores are matched by name.</p>
        <p>Test emails are sent only to the configured test address — never to real stores or HQ.</p>
      </div>
    </div>
  );
}


function APEmailSettings({ couriers: initCouriers, defaultCourier: initDefault, hoEmail: initHo, testEmail: initTest, onSaved }) {
  const [couriers, setCouriers] = useState(initCouriers || []);
  const [defaultCourier, setDefaultCourier] = useState(initDefault || "");
  const [hoEmail, setHoEmail] = useState(initHo || "");
  const [testEmail, setTestEmail] = useState(initTest || "");
  const [newCourierName, setNewCourierName] = useState("");
  const [newCourierUrl, setNewCourierUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const addCourier = () => {
    if (!newCourierName.trim()) return;
    setCouriers([...couriers, { name: newCourierName.trim(), tracking_url_template: newCourierUrl.trim() }]);
    setNewCourierName("");
    setNewCourierUrl("");
  };

  const removeCourier = (idx) => {
    const updated = couriers.filter((_, i) => i !== idx);
    setCouriers(updated);
    if (updated.length > 0 && !updated.find(c => c.name === defaultCourier)) {
      setDefaultCourier(updated[0].name);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ap/courier-settings`, { couriers, default_courier: defaultCourier, ho_email: hoEmail, test_email: testEmail });
      toast.success("AP email settings saved");
      onSaved?.();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="ap-email-settings">
      <h3 className="font-semibold text-sm text-slate-800">Email & Courier Settings</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-slate-600">Head Office Email</label>
          <Input value={hoEmail} onChange={e => setHoEmail(e.target.value)} placeholder="raj@company.com" className="mt-1" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Test Email (for testing only)</label>
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@company.com" className="mt-1" />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600 mb-2 block">Couriers</label>
        <div className="space-y-2">
          {couriers.map((c, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <Truck className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-700 w-24">{c.name}</span>
              <span className="text-xs text-slate-400 flex-1 truncate">{c.tracking_url_template || "No tracking URL"}</span>
              <input type="radio" name="ap-default-courier" checked={defaultCourier === c.name} onChange={() => setDefaultCourier(c.name)} className="mr-1" />
              <span className="text-[10px] text-slate-500 mr-2">Default</span>
              <button onClick={() => removeCourier(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Input value={newCourierName} onChange={e => setNewCourierName(e.target.value)} placeholder="Courier name (e.g. FedEx)" className="w-40 h-8 text-sm" />
          <Input value={newCourierUrl} onChange={e => setNewCourierUrl(e.target.value)} placeholder="Tracking URL template (use {awb})" className="flex-1 h-8 text-sm" />
          <Button size="sm" variant="outline" onClick={addCourier} disabled={!newCourierName.trim()} className="h-8"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
        </div>
      </div>

      <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ap-save-email-settings">
        <Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}
